<template>
	<div class="animated fadeIn">
		<b-card class="card-border mt-4">
			<b-card-title><i class="fa fa-bell"></i> Dispatch Alert Logs</b-card-title>
			<b-card-sub-title>Summary of all alerts reported from dispatches</b-card-sub-title>
			<div fluid class="px-2 mt-4">
				<loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

				<!-- Filter  -->
				<b-row class="my-2">
					<b-col sm="12" md="3" lg="3">
						<b-button v-b-popover.hover.right="'Toggle to show/hide filter options'" v-b-toggle.collapse-1
							class="filter">
							FILTER OPTIONS
						</b-button>
					</b-col>
					<b-col sm="12">
						<!-- Collapsible Filter Options -->
						<b-collapse id="collapse-1" class="mt-2" visible>
							<b-card>
								<!-- Date Range -->
								<b-row no-gutters>
									<b-col lg="4" md="5" sm="12" class="mr-4">
										<b-form-group label="Date From">
											<b-form-datepicker name="Date From" v-model="filterBy.dateFrom" locale="en"
												reset-button label-reset-button="Clear"
												:date-format-options="dateFormatOptions"
												:date-disabled-fn="dateFromDisabled" v-validate="'required'" />
											<span v-show="errors.has('Date From')" class="help-block">
												{{ errors.first('Date From') }}
											</span>
										</b-form-group>
									</b-col>
									<b-col lg="4" md="5" sm="12" class="mr-4">
										<b-form-group label="Date To">
											<b-form-datepicker name="Date To" v-model="filterBy.dateTo" locale="en"
												reset-button label-reset-button="Clear"
												:date-format-options="dateFormatOptions"
												:date-disabled-fn="dateFromDisabled" v-validate="'required'" />
											<span v-show="errors.has('Date To')" class="help-block">
												{{ errors.first('Date To') }}
											</span>
										</b-form-group>
									</b-col>
								</b-row>

								<!-- Dispatches -->
								<b-row no-gutters>
									<b-col lg="4" md="6" sm="12" class="mr-4">
										<b-form-group label="Plate No./Conduction No.">
											<v-select class="style-chooser" label="text" placeholder=" - Please select - " :options="plateNoOptions"
												:reduce="(plateNo) => plateNo.value" v-model="filterBy.plateNo">
												<template v-slot:no-options="{ search, searching }">
													<template v-if="searching">
														No results found for
														<em>
															<strong>{{ search }}</strong>
														</em>
													</template>
													<em :style="{ opacity: 0.5 }" v-else>
														Start typing to search for a plate number
													</em>
												</template>
											</v-select>
										</b-form-group>
									</b-col>
									<b-col lg="4" md="6" sm="12" class="mr-4">
										<b-form-group label="Driver">
											<v-select class="style-chooser" label="text" placeholder=" - Please select - " :options="driverOptions"
												:reduce="(driver) => driver.value" v-model="filterBy.driver">

												<template v-slot:no-options="{ search, searching }">
													<template v-if="searching">
														No results found for
														<em>
															<strong>{{ search }}</strong>
														</em>
													</template>
													<em :style="{ opacity: 0.5 }" v-else>
														Start typing to search for a driver
													</em>
												</template>
											</v-select>
										</b-form-group>
									</b-col>
								</b-row>

								<b-row no-gutters>
									<b-col lg="4" md="6" sm="12" class="mr-4">
										<b-form-group label="Dispatch No."
											description="NOTE: Input the exact Dispatch No. to search">
											<b-form-input id="dispatchNo" name="Dispatch No" type="search"
												class="numFont" v-model="filterBy.dispatchNo"
												v-validate="getValidationParam(false, /^[A-Z0-9]{2}-[0-9]{6}-[0-9]{1,4}$/)"
												placeholder="XX-YYMMMDD-000" autocomplete="off" />
											<span v-show="errors.has('Dispatch No')" class="help-block">
												{{ errors.first('Dispatch No') }}
											</span>
										</b-form-group>
									</b-col>
								</b-row>

								<b-row no-gutters>
									<b-col sm="12">
										<b-button class="mr-1" variant="success" @click="onFilterRequest">
											Generate
										</b-button>
										<b-button class="mr-1" variant="primary" @click="resetFilters">
											Reset
										</b-button>
									</b-col>
								</b-row>
							</b-card>
						</b-collapse>
					</b-col>
				</b-row>

				<div v-if="items.length > 0">
					<b-row>
						<b-col sm="6" md="3" class="mt-4 mb-2">
							<b-dropdown id="dispatch-alert-select-actions" text=" Select Actions " variant="dark" slot="append">
								<b-dropdown-item>
									<json-excel :data="exportData" :fields="exportFields" type="xls"
										:name="fileName + '.xls'">
										Export Dispatch Alert Logs in Excel
									</json-excel>
								</b-dropdown-item>
								<b-dropdown-item>
									<json-excel :data="exportData" :fields="exportFields" type="csv"
										:name="fileName + '.csv'">
										Export Dispatch Alert Logs to CSV
									</json-excel>
								</b-dropdown-item>
							</b-dropdown>
						</b-col>

						<b-col sm="6" md="4" offset-md="5" class="mt-4 mb-2 text-md-right">
							<b-input-group prepend="Show" append="/ Page">
								<b-form-select :options="pageOptions" v-model="perPage" />
							</b-input-group>
						</b-col>
					</b-row>

					<b-table show-empty striped hover :items="items" :fields="fields" :current-page="currentPage"
						:per-page="perPage" :filter="filter" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
						:sort-direction="sortDirection" responsive>

						<template v-slot:cell(dispatchNo)="row">
							<span class="numFont">{{ row.item.dispatchNo }}</span>
						</template>

						<template v-slot:cell(plateNo)="row">
							<span class="numFont">{{ row.item['Plate No./Conduction No.'] }}</span>
						</template>

						<template v-slot:cell(dateCreated)="row">
							{{ row.item.dateCreated ? getDisplayDateTime(row.item.dateCreated) : '-' }}
						</template>

						<template v-slot:cell(geolocation)="row">
							{{ getCoordinates(row.item.geoaddress) }}
						</template>

						<template v-slot:cell(actions)="row">
							<span class="text-nowrap">
								<b-button size="sm" v-b-tooltip.hover.top="'Show/Hide Other Details'" variant="dark"
									@click.stop="row.toggleDetails" class="mr-1">
									<i class="fa fa-eye-slash" v-if="row.detailsShowing"></i>
									<i class="fa fa-eye" v-else></i>
								</b-button>
							</span>
						</template>

						<template slot="row-details" slot-scope="row">
							<DispatchAlertDetailsView :row="row" />
						</template>
					</b-table>

					<b-row>
						<b-col md="8" sm="12" class="my-1">
							<span class="total-display">Total: {{ totalRows ? totalRows.toLocaleString() : 0 }}</span>
						</b-col>
						<b-col md="4" sm="12" class="my-1">
							<b-pagination align="right" :total-rows="totalRows" :per-page="perPage"
								v-model="currentPage" class="my-0" />
						</b-col>
					</b-row>
				</div>
				<div v-else>
					<b-card> No Results Found. </b-card>
				</div>

			</div>
		</b-card>

		<!-- Modals here -->
		<DispatchAlertReportLocationView />

	</div>
</template>

<script>
// Components
import DispatchAlertDetailsView from '@/views/reports/dispatchAlert/DispatchAlertDetailsView';
import DispatchAlertReportLocationView from '@/views/reports/dispatchAlert/DispatchAlertReportLocationView';

// Util
import { DropDownItemsUtil } from '@/utils/dropDownItemsUtil';
import { DateUtil } from '@/utils/dateutil';
import { LocationUtil } from '@/utils/locationUtil';

// API
import reportApi from '@/api/reportApi';

// Others
import JsonExcel from 'vue-json-excel';
import config from '@/config/env-constants';
import Loading from 'vue-loading-overlay';
import moment from 'moment';
import 'vue-loading-overlay/dist/vue-loading.css';
import _ from 'lodash';

export default {
	name: 'dispatch-alert-logs',
	components: {
		DispatchAlertDetailsView,
		DispatchAlertReportLocationView,
		JsonExcel,
		Loading
	},
	data() {
		return {
			items: [],
			fields: [
				{
					key: 'dispatchNo',
					label: 'Dispatch No.',
					sortable: true,
				},
				{
					key: 'Date Created',
					sortable: true,
				},
				{
					key: 'alertIntervalNo',
					label: 'Alert Interval No',
					sortable: true,
				},
				{
					key: 'Driver',
					sortable: true,
				},
				{
					key: 'plateNo',
					label: 'Plate No./Conduction No.',
					sortable: true,
				},
				{
					key: 'geolocation',
					sortable: true,
				},
				{
					key: 'actions',
					thClass: 'text-center'
				}
			],
			currentPage: 1,
			perPage: 10,
			totalRows: 0,
			pageOptions: [5, 10, 15, 25, 50, 100],
			sortBy: null,
			sortDesc: false,
			sortDirection: 'asc',
			filter: null,

			dateFormatOptions: { ...config.dateFormatOptions },

			defaultFilterBy: {
				dateFrom: moment().format('YYYY-MM-DD'),
				dateTo: moment().format('YYYY-MM-DD'),
				plateNo: config.transportationDefaultValue,
				driver: config.driverDefaultValue,
				dispatchNo: ''
			},

			filterBy: {
				dateFrom: moment().format('YYYY-MM-DD'),
				dateTo: moment().format('YYYY-MM-DD'),
				plateNo: config.transportationDefaultValue,
				driver: config.driverDefaultValue,
				dispatchNo: ''
			},

			prevFilterBy: {},
			params: {},

			allDriversObj: {},
			allTransportationsObj: {},
			allDispatchAlertsObj: {},

			plateNoOptions: [],
			driverOptions: [],

			isSuperAdmin: this.$store.getters.isSuperAdmin,
			loggedUser: this.$store.getters.loggedUser,
			loggedUserCompany: this.$store.getters.loggedUserCompany,

			isLoading: false,
		};
	},
	computed: {
		dateFrom() {
			const dateTo = moment();
			const dateFrom = dateTo.add(-30, 'days');
			return dateFrom.format('YYYY-MM-DD');
		},
		dateTo() {
			return moment().format('YYYY-MM-DD');
		},

		exportData() {
			return this.items;
		},

		exportFields() {
			return {
				'Dispatch No.': 'dispatchNo',
				'Date Created': 'Date Created',
				'Alert Interval No': 'alertIntervalNo',
				'Driver': 'driver.name',
				'Plate No./Conduction No.': 'transportation.plateNo',
				'Latitude': 'geoaddress.latitude',
				'Longitude': 'geoaddress.longitude',
			};
		},

		fileName() {
			let currTimeStamp = DateUtil.getCurrentTimestamp();
			return 'Dispatch-Alert-' + DateUtil.getDateInDDMMYYYYHHSSFormat(currTimeStamp);
		},
	},
	created() {
		// Set Default Date Range
		this.defaultFilterBy.dateFrom = this.dateFrom;
		this.defaultFilterBy.dateTo = this.dateTo;
	},
	mounted() {
		setTimeout(async () => {
			try {
				// Filter Access
				if (this.$store.getters.isScanner || this.$store.getters.isRetailer) {
					this.$router.push('/dashboard');
					this.$toaster.warning('You are not allowed to access this page.');
				}

				// show loading indicator
				this.isLoading = true;

				this.allTransportationsObj = { ...this.$store.getters.transportations };
				this.allDriversObj = { ...this.$store.getters.driverUsers };

				this.plateNoOptions = DropDownItemsUtil.retrieveTransportations(this.allTransportationsObj, false);

				if (this.isSuperAdmin) {
					this.driverOptions = DropDownItemsUtil.retrieveActiveDrivers(this.allDriversObj, false);
				} else {
					this.driverOptions = DropDownItemsUtil.retrieveActiveDriversByCompany(this.allDriversObj, this.loggedUserCompany);
				}

				await this.resetFilters();
				
			} catch (error) {
				this.$toaster.error('Error loading data. Please reload the page again.');
			} finally {
				// hide loading indicator
				this.isLoading = false;
			}

		}, config.timeout);
	},
	methods: {
		getValidationParam(isRequired, regex) {
			return {
				required: isRequired,
				regex: regex,
			};
		},
		getCoordinates(geoaddress) {
			return LocationUtil.getCoordinates(geoaddress);
		},

		dateFromDisabled(_ymd, date) {
			return date > new Date();
		},
		validateFilter() {
			let isValid = true;

			if (_.isEmpty(this.filterBy.dateFrom) && _.isEmpty(this.filterBy.dateTo)) {
				this.$toaster.warning('Date From and Date To are required.');
				isValid = false;
			} else if ((_.isEmpty(this.filterBy.dateFrom) && !_.isEmpty(this.filterBy.dateTo)) ||
				(!_.isEmpty(this.filterBy.dateFrom) && _.isEmpty(this.filterBy.dateTo))) {
				this.$toaster.warning('Invalid Date Range. Date From and Date To must both have value.');
				isValid = false;
			} else if (this.filterBy.dateFrom > this.filterBy.dateTo) {
				this.$toaster.warning('Invalid Date Range. Date From must be less than Date To.');
				isValid = false;
			} else if (DateUtil.getNoOfDays(this.filterBy.dateFrom, this.filterBy.dateTo) > 90) {
				this.$toaster.warning('Invalid Date Range. Data range is allowed up to 90 days difference.');
				isValid = false;
			}

			return isValid;
		},

		async onFilterRequest() {
			let isValid = await this.$validator.validateAll();
			if (!isValid) {
				this.$toaster.warning('Please address the field/s with invalid input.');
				// hide loading indicator
				this.isLoading = false;
				return;
			}

			if (!this.validateFilter()) {
				return;
			}

			if (!_.isEqual(this.filterBy, this.prevFilter)) {
				this.retrieveData();
				this.prevFilter = { ...this.filterBy };
			}
		},
		async resetFilters() {
			if (!_.isEqual(this.filterBy, this.defaultFilterBy)) {
				// reset to default
				this.filterBy = { ...this.defaultFilterBy };
				this.prevFilter = { ...this.filterBy };

				// reset validation
				this.$validator.reset();
				this.errors.clear();

				await this.retrieveData();
			}
		},
		async retrieveData() {
			try {
				// show loading indicator
				this.isLoading = true;

				let filter = { ...this.filterBy };
				filter.fromTimestamp = DateUtil.startDateTimeStamp(new Date(filter.dateFrom));
				filter.toTimestamp = DateUtil.endDateTimeStamp(new Date(filter.dateTo));

				let view = this.isSuperAdmin ? config.view.ADMIN : config.view.COMPANY;

				filter.companyId = this.loggedUserCompany.id;
				let { data } = await reportApi.getDispatchAlertsReport(
					filter,
					view,
					this.loggedUser.id
				);

				this.allDispatchAlertsObj = data.dispatchAlerts;

				if (_.isEmpty(this.allDispatchAlertsObj)) {
					this.$toaster.warning('No Results found.');
				} else {
					this.processDispatchAlerts(this.allDispatchAlertsObj);
				}

			} catch (error) {
				this.$toaster.error('Error loading data. Please reload the page again.');
			} finally {
				// hide loading indicator
				this.isLoading = false;
			}
		},

		processDispatchAlerts(dispatchAlerts) {
			this.allDispatchAlertsObj = dispatchAlerts;

			let filteredObjs = { ...dispatchAlerts };
			_.forEach(filteredObjs, (dispatchAlert, id) => {
				let dispatchNo = this.filterBy.dispatchNo;
				if (dispatchNo && dispatchNo.length > 0 && dispatchNo !== dispatchAlert.dispatchNo) {
					delete filteredObjs[id];
				}

				let plateNo = this.filterBy.plateNo;
				if (plateNo && plateNo.id && plateNo.id.length > 0 &&
					plateNo.name !== dispatchAlert.transportation.plateNo) {
					delete filteredObjs[id];
				}

				let driver = this.filterBy.driver;
				if (driver && driver.id && driver.id !== dispatchAlert.driver.userId) {
					delete filteredObjs[id];
				}
			});

			this.items = Object.values(filteredObjs);
			this.items.forEach((item) => {
				item['Driver'] = item.driver.name;
				item['Plate No./Conduction No.'] = item.transportation.plateNo;
				item['Date Created'] = this.getDisplayDateTime(item.dateCreated);
			});
			this.items = _.sortBy(this.items, ['Dispatch ID']);
			this.totalRows = this.items.length;

			// update cache
			this.$store.dispatch('setAllDispatchAlertReports', this.allDispatchAlertsObj);
		},

		// UTILS
		getDisplayDateTime(date) {
			return DateUtil.getFormattedDateWithTime(date);
		},
	}
};
</script>